import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Badge, Box, ButtonBase, CircularProgress, Container, Typography } from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import { createParticipantApi, errorMessage, imageUrl, loadSession, saveSession } from './participantApi';
import LoginScreen from './components/LoginScreen';
import ProfileEditor from './components/ProfileEditor';
import ParticipantsTab from './components/ParticipantsTab';
import CardsTab from './components/CardsTab';
import InstallApp, { usePwaHead } from './components/InstallApp';
import LogoutIcon from '@mui/icons-material/Logout';
import { IconButton } from '@mui/material';
import { ACCENT, ACCENT_SOFT, BORDER, Card, GradientButton, INK, MUTED, PageBackground, TextButton, Toast } from './ui';

const TABS = { participants: 'participants', requests: 'requests', contacts: 'contacts', profile: 'profile' };

const TAB_ITEMS = [
  { id: TABS.participants, label: 'Участники', icon: PeopleAltOutlinedIcon },
  { id: TABS.requests, label: 'Запросы', icon: MarkEmailUnreadOutlinedIcon },
  { id: TABS.contacts, label: 'Контакты', icon: ContactsOutlinedIcon },
  { id: TABS.profile, label: 'Анкета', icon: AccountCircleOutlinedIcon },
];

const Centered = ({ children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 2, textAlign: 'center' }}>
    {children}
  </Box>
);

const TabBadge = ({ id, incoming, children }) => (
  <Badge color='error' badgeContent={incoming} invisible={id !== TABS.requests || !incoming}
         sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
    {children}
  </Badge>
);

// Разделы на десктопе — сегментированное меню под шапкой.
const SectionTabs = ({ tab, onChange, incoming }) => (
  <Card sx={{ p: '4px !important', mb: 2, display: { xs: 'none', sm: 'flex' }, gap: 0.5 }}>
    {TAB_ITEMS.map(({ id, label, icon: Icon }) => {
      const active = tab === id;
      return (
        <ButtonBase
          key={id}
          onClick={() => onChange(id)}
          sx={{
            flex: 1,
            minWidth: 0,
            borderRadius: '10px',
            py: 1,
            gap: 1,
            fontSize: 14,
            fontWeight: 600,
            color: active ? '#fff' : MUTED,
            background: active ? ACCENT : 'transparent',
            transition: 'background .2s, color .2s',
          }}
        >
          <TabBadge id={id} incoming={incoming}>
            <Icon sx={{ fontSize: 20 }} />
          </TabBadge>
          <span>{label}</span>
        </ButtonBase>
      );
    })}
  </Card>
);

// Разделы на телефоне — нижнее меню, как в мобильных приложениях (под большим пальцем).
const BottomNav = ({ tab, onChange, incoming }) => (
  <Box
    component='nav'
    sx={{
      display: { xs: 'flex', sm: 'none' },
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 10,
      bgcolor: '#fff',
      borderTop: `1px solid ${BORDER}`,
      pb: 'env(safe-area-inset-bottom)',
    }}
  >
    {TAB_ITEMS.map(({ id, label, icon: Icon }) => {
      const active = tab === id;
      return (
        <ButtonBase
          key={id}
          onClick={() => onChange(id)}
          sx={{
            flex: 1,
            flexDirection: 'column',
            gap: 0.25,
            py: 0.75,
            fontSize: 11,
            fontWeight: active ? 700 : 500,
            color: active ? INK : MUTED,
          }}
        >
          <TabBadge id={id} incoming={incoming}>
            <Icon sx={{ fontSize: 24 }} />
          </TabBadge>
          <span>{label}</span>
        </ButtonBase>
      );
    })}
  </Box>
);

// EV-85: публичная страница веб-нетворкинга участника /app/net/:token.
// Интро → вход по почте и коду → анкета → участники / запросы / контакты.
const NetworkingWeb = () => {
  const { token } = useParams();
  const location = useLocation();
  // своё уведомление вместо общего notistack: мягкие цвета и позиция сверху (снизу — меню)
  const [toast, setToast] = useState(null);
  const enqueueSnackbar = useCallback((text, { variant } = {}) => {
    const id = Date.now();
    setToast({ id, text, variant });
    setTimeout(() => setToast(t => (t && t.id === id ? null : t)), 2600);
  }, []);

  const [session, setSession] = useState(() => loadSession(token));
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState(null);
  const [stage, setStage] = useState(session ? 'app' : 'intro');
  const [profile, setProfile] = useState(null);
  // EV-86: ссылка из письма о запросах ведёт сразу в раздел: ?tab=requests
  const [tab, setTab] = useState(() => {
    const wanted = new URLSearchParams(location.search).get('tab');
    return Object.values(TABS).includes(wanted) ? wanted : TABS.participants;
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [incoming, setIncoming] = useState(0);
  const [busy, setBusy] = useState(false);

  const dropSession = useCallback(() => {
    saveSession(token, null);
    setSession(null);
    setProfile(null);
    setStage('intro');
  }, [token]);

  const api = useMemo(() => createParticipantApi(session, dropSession), [session, dropSession]);

  useEffect(() => {
    api
      .getParticipantNetworkingInfo(token)
      .then(({ data }) => {
        setInfo(data);
        if (data.meetupName) document.title = 'Нетворкинг — ' + data.meetupName;
      })
      .catch(err => setInfoError(err?.response?.status === 404 ? 'Страница не найдена' : errorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!session || !info || !info.enabled) return;
    api
      .getParticipantProfile(token)
      .then(({ data }) => {
        setProfile(data);
        setStage('app');
        if (!data.published) setTab(TABS.profile);
      })
      // истёкшую сессию обработает перехватчик (вернёт к входу), остальное — показываем
      .catch(err => {
        if (err?.response?.status !== 401) enqueueSnackbar(errorMessage(err), { variant: 'error' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, session, info, token]);

  useEffect(() => {
    if (stage !== 'app' || !profile || !profile.published) {
      setIncoming(0);
      return;
    }
    api
      .listIncomingRequests(token)
      .then(({ data }) => setIncoming(data.length))
      .catch(() => {});
  }, [api, stage, profile, token, refreshKey]);

  usePwaHead(info, token);

  // тип поля по ключу — чтобы показывать множественный выбор «таблетками»
  const kinds = useMemo(() => {
    const ret = {};
    ((info && info.fields) || []).forEach(f => {
      ret[f.key] = f.kind;
    });
    return ret;
  }, [info]);

  const loggedIn = sessionToken => {
    saveSession(token, sessionToken);
    setSession(sessionToken);
  };

  const logout = () => {
    api.logoutParticipant(token).catch(() => {});
    dropSession();
  };

  const act = (call, successText) => card => {
    if (busy) return;
    setBusy(true);
    call(token, card.visitorId)
      .then(() => {
        setRefreshKey(k => k + 1);
        enqueueSnackbar(successText, { variant: 'success' });
      })
      .catch(err => enqueueSnackbar(errorMessage(err), { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  const onRequest = act((t, id) => api.requestParticipantContact(t, id), 'Запрос отправлен');
  const onAccept = act((t, id) => api.acceptParticipantRequest(t, id), 'Контакт добавлен в «Контакты»');
  const onDecline = act((t, id) => api.declineParticipantRequest(t, id), 'Запрос отклонён');

  if (infoError) {
    return (
      <PageBackground>
        <Centered>
          <Typography variant='h4'>{infoError}</Typography>
        </Centered>
      </PageBackground>
    );
  }
  if (!info) {
    return (
      <PageBackground>
        <Centered>
          <CircularProgress />
        </Centered>
      </PageBackground>
    );
  }

  // Шапка — одна компактная строка: логотип | «Нетворкинг» + название | выход.
  // На телефоне закреплена сверху, логотип и название выровнены по одной оси.
  const loggedInApp = stage === 'app' && profile;
  const header = (
    <Box
      sx={{
        position: { xs: 'sticky', sm: 'static' },
        top: 0,
        zIndex: 10,
        bgcolor: { xs: '#fff', sm: 'transparent' },
        borderBottom: { xs: `1px solid ${BORDER}`, sm: 'none' },
      }}
    >
      <Container maxWidth='md' sx={{ px: { xs: 1.5, sm: 3 }, maxWidth: { md: 760 } }}>
        <Box sx={{ py: { xs: 1, sm: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 } }}>
          {info.logo && (
            <Box
              component='img'
              src={imageUrl(info.logo)}
              alt=''
              sx={{ height: { xs: 36, sm: 52 }, maxWidth: { xs: 90, sm: 140 }, objectFit: 'contain', flexShrink: 0 }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 10, sm: 12 }, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: MUTED, lineHeight: 1.3 }}>
              Нетворкинг
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 16, sm: 26 },
                fontWeight: 800,
                lineHeight: 1.2,
                color: INK,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {info.meetupName}
            </Typography>
          </Box>
          {loggedInApp && (
            <>
              <InstallApp info={info} token={token} />
              <IconButton onClick={logout} aria-label='Выйти' sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: MUTED }}>
                <LogoutIcon fontSize='small' />
              </IconButton>
              <TextButton size='small' onClick={logout} sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}>
                Выйти
              </TextButton>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );

  let content;
  if (!info.enabled) {
    content = (
      <Card>
        <Typography variant='h5'>Нетворкинг этого мероприятия сейчас недоступен.</Typography>
      </Card>
    );
  } else if (stage === 'intro') {
    content = (
      <Card sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              background: ACCENT_SOFT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <HandshakeOutlinedIcon sx={{ color: INK, fontSize: 21 }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK }}>Нетворкинг</Typography>
        </Box>
        <Typography sx={{ whiteSpace: 'pre-line', mb: 2.5, fontSize: { xs: 15, sm: 17 }, lineHeight: 1.55, color: INK }}>
          {info.introText ||
            'Найдите интересных участников мероприятия и обменяйтесь контактами. Контакты открываются, только когда второй участник принимает ваш запрос.'}
        </Typography>
        <GradientButton onClick={() => setStage('login')} sx={{ px: 4, width: { xs: '100%', sm: 'auto' } }}>
          Участвовать
        </GradientButton>
      </Card>
    );
  } else if (stage === 'login' || !session) {
    content = <LoginScreen api={api} token={token} onLoggedIn={loggedIn} onBack={() => setStage('intro')} />;
  } else if (!profile) {
    content = (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  } else {
    const locked = !profile.published && (tab === TABS.participants || tab === TABS.requests);
    content = (
      <>
        <SectionTabs tab={tab} onChange={setTab} incoming={incoming} />
        <BottomNav tab={tab} onChange={setTab} incoming={incoming} />

        {/* анкеты других открываются только после публикации своей (сервер тоже это проверяет) */}
        {locked && (
          <Card sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, mb: 1 }}>Заполните и опубликуйте свою анкету</Typography>
            <Typography sx={{ color: MUTED, mb: 2, fontSize: 14 }}>
              После публикации откроется доступ к анкетам других участников и запросам контактов.
            </Typography>
            <GradientButton onClick={() => setTab(TABS.profile)}>Перейти к анкете</GradientButton>
          </Card>
        )}
        {!locked && tab === TABS.participants && (
          <ParticipantsTab
            api={api}
            token={token}
            refreshKey={refreshKey}
            kinds={kinds}
            busy={busy}
            onRequest={onRequest}
            onAccept={onAccept}
            onDecline={onDecline}
          />
        )}
        {!locked && tab === TABS.requests && (
          <CardsTab
            load={() => api.listIncomingRequests(token)}
            refreshKey={refreshKey}
            kinds={kinds}
            hint='Эти участники хотят обменяться с вами контактами. Ваши контакты они увидят, только если вы примете запрос.'
            emptyText='Новых запросов нет.'
            busy={busy}
            onAccept={onAccept}
            onDecline={onDecline}
          />
        )}
        {tab === TABS.contacts && (
          <CardsTab
            load={() => api.listParticipantContacts(token)}
            refreshKey={refreshKey}
            kinds={kinds}
            countLabel='Контактов'
            hint='Участники, с которыми вы обменялись контактами.'
            emptyText='Пока нет контактов. Запросите контакт у интересных участников или примите входящий запрос.'
            contactsOpen
            exportFileName={`Контакты — ${info.meetupName || 'нетворкинг'}.xlsx`}
          />
        )}
        {tab === TABS.profile && (
          <ProfileEditor
            api={api}
            token={token}
            info={info}
            profile={profile}
            onProfileChange={p => {
              setProfile(p);
              setRefreshKey(k => k + 1);
            }}
            onPublished={() => {
              enqueueSnackbar('Анкета опубликована', { variant: 'success' });
              setTab(TABS.participants);
            }}
          />
        )}
      </>
    );
  }
  // до входа — заметная карточка «Установить приложение», после входа — скромная кнопка в шапке
  if (info.enabled && (stage === 'intro' || stage === 'login' || !session)) {
    content = (
      <>
        {content}
        <InstallApp info={info} token={token} variant='prominent' />
      </>
    );
  }

  return (
    // на телефоне снизу место под нижнее меню разделов
    <PageBackground sx={{ pb: { xs: loggedInApp ? 11 : 4, sm: 6 } }}>
      {header}
      <Toast toast={toast} />
      <Container maxWidth='md' sx={{ px: { xs: 1.5, sm: 3 }, pt: { xs: 1.5, sm: 0 }, maxWidth: { md: 760 } }}>
        {content}
      </Container>
    </PageBackground>
  );
};

export default NetworkingWeb;
